/* eslint-disable consistent-return */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Add, Clear } from '@mui/icons-material';
import {
  Dialog,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import * as colors from 'commons/styles/colors';
import { InventoryAdjustmentItems, ProductDetail } from 'commons/types';
import {
  getInventoryAdjustmentBin,
  getInventoryAdjustmentProductDetail,
  postInventoryAdjustmentItem,
} from 'redux-stores/actions';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';

import { InventoryDetailModalStyle as S } from '../styles/InventoryDetailModal.styles';

interface InventoryAdjustmentBatchInfo {
  bin: string;
  inventory_number: string;
  expiry_date: string;
  quantity: number;
}

export interface IInventoryDetailModalProps {
  onClose: () => void;
  openModal: boolean;
  items: InventoryAdjustmentItems;
  itemsIndex: number;
  documentNumber: string;
}

interface FieldErrors {
  [key: number]: string;
}

export const InventoryDetailModal: React.FC<IInventoryDetailModalProps> = ({
  onClose,
  openModal,
  items,
  itemsIndex,
  documentNumber,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const defaultInventoryAdjustmentItemInfo: InventoryAdjustmentBatchInfo[] = [
    {
      bin: '',
      inventory_number: '',
      expiry_date: '',
      quantity: 0,
    },
  ];

  const [productDetailErrors, setProductDetailErrors] = useState<FieldErrors>(
    {},
  );
  const [binErrors, setBinErrors] = useState<FieldErrors>({});
  const [quantityErrors, setQuantityErrors] = useState<FieldErrors>({});
  const [isTotalQtyError, setIsTotalQtyError] = useState<boolean>(false);

  const [
    inventoryAdjustmentItemProcessState,
    setInventoryAdjustmentItemProcessState,
  ] = useState<InventoryAdjustmentBatchInfo[]>(
    defaultInventoryAdjustmentItemInfo,
  );

  useEffect(() => {
    if (items.details.length > 0) {
      setInventoryAdjustmentItemProcessState(
        items.details.map((detail) => {
          return {
            ...detail,
            expiry_date:
              detail.bin && detail.expiry_date
                ? new Date(detail.expiry_date).toISOString()
                : '',
          };
        }),
      );
    }
  }, [items.details, items.sku_code]);

  const debouncedValidateProductDetail = useDebouncedCallback(
    async (params: {
      binNumber: string;
      skuCode: string;
      quantity: number;
      inventoryNumber: string;
      state: InventoryAdjustmentBatchInfo[];
      index: number;
    }) => {
      const { state, index, binNumber, skuCode, quantity, inventoryNumber } =
        params;
      const result = await getInventoryAdjustmentProductDetail(
        {
          location: items.location,
          bin: binNumber,
          sku_code: skuCode,
          inventory_number: inventoryNumber,
        },
        items.quantity_adjustment,
        dispatch,
      );

      const clonedAdjusted = structuredClone(state);
      if (result) {
        clonedAdjusted[index].expiry_date = result.expiry_date.toString();
        clonedAdjusted[index].bin = result.bin;
        clonedAdjusted[index].inventory_number = result.inventory_number;
        clonedAdjusted[index].quantity = quantity;
        setInventoryAdjustmentItemProcessState(clonedAdjusted);
        setProductDetailErrors((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });

        if (
          clonedAdjusted[index].quantity &&
          clonedAdjusted[index].quantity < 0 &&
          Math.abs(clonedAdjusted[index].quantity) > result.quantity
        ) {
          setQuantityErrors((prev) => ({
            ...prev,
            [index]: `You only have ${result.quantity} available in this bin`,
          }));
        } else if (
          clonedAdjusted[index].quantity * items.quantity_adjustment <
          0
        ) {
          setQuantityErrors((_prev) => ({
            ..._prev,
            [index]: `Invalid number (must be ${
              items.quantity_adjustment > 0 ? 'positive' : 'negative'
            })`,
          }));
        } else {
          setQuantityErrors((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
        }
      } else if (items.quantity_adjustment < 0) {
        clonedAdjusted[index].expiry_date = '';
        clonedAdjusted[index].bin = '';
        clonedAdjusted[index].inventory_number = '';
        setInventoryAdjustmentItemProcessState(clonedAdjusted);
        setProductDetailErrors((prev) => ({
          ...prev,
          [index]: 'Product Detail not found',
        }));
      }
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const debouncedValidateBin = useDebouncedCallback(
    async (params: {
      binNumber: string;
      state: InventoryAdjustmentBatchInfo[];
      index: number;
    }) => {
      const { state, index, binNumber } = params;
      const clonedAdjusted = structuredClone(state);
      const binSearchResult = await getInventoryAdjustmentBin(
        {
          location: items.location,
          bin: binNumber,
        },
        dispatch,
      );

      if (!binSearchResult) {
        clonedAdjusted[index].bin = '';
        setInventoryAdjustmentItemProcessState(clonedAdjusted);
        setBinErrors((prev) => ({
          ...prev,
          [index]: 'Bin not found',
        }));
      } else {
        setBinErrors((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);

  const isSaveButtonDisabled = !inventoryAdjustmentItemProcessState.every(
    (detail) =>
      ((detail.bin &&
        detail.inventory_number &&
        detail.expiry_date &&
        detail.quantity) ||
        (!detail.bin &&
          !detail.inventory_number &&
          !detail.expiry_date &&
          !detail.quantity)) &&
      Object.keys(productDetailErrors).length === 0 &&
      Object.keys(binErrors).length === 0 &&
      Object.keys(quantityErrors).length === 0 &&
      !isTotalQtyError,
  );

  const handleSave = (): void => {
    postInventoryAdjustmentItem(
      {
        document_number: documentNumber,
        index: itemsIndex,
        item: {
          ...items,
          details: inventoryAdjustmentItemProcessState
            .filter((item) => item.bin)
            .map((item) => {
              return { ...item, expiry_date: new Date(item.expiry_date) };
            }),
        },
      },
      dispatch,
    );
    onClose();
  };

  const renderInventoryAdjustmentItemProcessForm = (
    state: InventoryAdjustmentBatchInfo[],
    maxQuantity: number = Number.POSITIVE_INFINITY,
  ): React.JSX.Element[] => {
    return state.map((detail, idx) => {
      const onFieldChange = (
        type: keyof Pick<
          ProductDetail,
          'bin' | 'inventory_number' | 'expiry_date' | 'quantity'
        >,
        value: string,
        nextField?: string,
      ): void => {
        const clonedAdjusted = structuredClone(state);
        const prevValue = clonedAdjusted[idx][type];
        const cleanNewValue = value.trim();

        if (
          /*
          data type number
          need to be in this chained OR format, so the else block could executed properly 
          */
          type === 'quantity'
        ) {
          if (!Number.isNaN(+cleanNewValue)) {
            const fieldQuantity = parseInt(cleanNewValue, 10);
            const newQuantity =
              fieldQuantity > maxQuantity ? maxQuantity : fieldQuantity;
            clonedAdjusted[idx].quantity = newQuantity;
          }

          const totalQuantity = clonedAdjusted.reduce(
            (totalQty, _detail) => totalQty + _detail.quantity,
            0,
          );
          if (Math.abs(totalQuantity) > Math.abs(items.quantity_adjustment)) {
            dispatch(
              snackbarSetData({
                open: true,
                message:
                  'Total quantity is bigger than requested quantity for this item',
                color: colors.PRIMARY_RED,
              }),
            );
            setIsTotalQtyError(true);
          } else {
            setIsTotalQtyError(false);
          }
        } else {
          clonedAdjusted[idx][type] = cleanNewValue;
        }

        setInventoryAdjustmentItemProcessState(clonedAdjusted);

        /* debounce after state assignment to prevent race condition */
        const {
          bin,
          inventory_number: inventoryNumber,
          quantity,
        } = clonedAdjusted[idx];
        if (
          (type === 'bin' ||
            type === 'inventory_number' ||
            type === 'quantity') &&
          bin &&
          inventoryNumber
        ) {
          debouncedValidateProductDetail({
            binNumber: bin,
            inventoryNumber,
            quantity,
            skuCode: items.sku_code,
            index: idx,
            state,
          });
        }
        if (type === 'bin' && bin && items.quantity_adjustment >= 0) {
          debouncedValidateBin({
            binNumber: bin,
            index: idx,
            state,
          });
        }

        if (
          nextField &&
          (value.endsWith('\t') ||
            value.endsWith('\n') ||
            (!prevValue && value.length > 1))
        ) {
          debouncedFocusNextField(
            `input[id="field-adjust-${nextField}-${idx}"]`,
          );
        }
      };

      const clearField = (
        field: keyof Pick<
          ProductDetail,
          'bin' | 'inventory_number' | 'expiry_date' | 'quantity'
        >,
      ): void => {
        const clonedAdjusted = structuredClone(state);
        if (field === 'bin' || field === 'inventory_number') {
          clonedAdjusted[idx] = {
            bin: '',
            inventory_number: '',
            expiry_date: '',
            quantity: 0,
          };
        } else if (field === 'expiry_date') {
          clonedAdjusted[idx][field] = '';
        } else {
          clonedAdjusted[idx][field] = 0;
        }
        setInventoryAdjustmentItemProcessState(clonedAdjusted);
      };

      return (
        <Grid container style={{ marginBottom: '4px' }}>
          <S.FieldRowWrapper item xs={3}>
            Serial Batch Number
            <TextField
              variant="outlined"
              placeholder="-"
              value={detail.inventory_number}
              error={!!productDetailErrors[idx]}
              helperText={productDetailErrors[idx]}
              onChange={(event) =>
                onFieldChange('inventory_number', event.target.value, 'bin')
              }
              id={`field-adjust-batch-${idx}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => clearField('inventory_number')}>
                      <Clear style={{ color: colors.PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
              fullWidth
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={3}>
            Bin
            <TextField
              variant="outlined"
              placeholder="-"
              value={detail.bin}
              error={
                items.quantity_adjustment >= 0
                  ? !!binErrors[idx]
                  : !!productDetailErrors[idx]
              }
              helperText={
                items.quantity_adjustment >= 0
                  ? binErrors[idx]
                  : productDetailErrors[idx]
              }
              onChange={(event) =>
                onFieldChange('bin', event.target.value, 'expiry_date')
              }
              id={`field-adjust-bin-${idx}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => clearField('bin')}>
                      <Clear style={{ color: colors.PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
              fullWidth
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={3}>
            Exp Date
            <DateField
              variant="outlined"
              defaultValue={null}
              value={
                detail.expiry_date ? DateTime.fromISO(detail.expiry_date) : null
              }
              onChange={(value) =>
                onFieldChange(
                  'expiry_date',
                  value?.toISODate() || '',
                  'quantity',
                )
              }
              id={`field-adjust-expiry-${idx}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => clearField('expiry_date')}>
                      <Clear style={{ color: colors.PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
              fullWidth
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={3}>
            QTY
            <TextField
              variant="outlined"
              placeholder="0"
              type="number"
              value={detail.quantity || ''}
              error={!!quantityErrors[idx]}
              helperText={quantityErrors[idx]}
              onChange={(event) =>
                onFieldChange('quantity', event.target.value)
              }
              id={`field-adjust-quantity-${idx}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => onFieldChange('quantity', '0')}>
                      <Clear style={{ color: colors.PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
              fullWidth
            />
          </S.FieldRowWrapper>
        </Grid>
      );
    });
  };

  const addBatch = (state: InventoryAdjustmentBatchInfo[]): void => {
    const updatedAdjusted = [
      ...state,
      {
        bin: '',
        inventory_number: '',
        expiry_date: '',
        quantity: 0,
      },
    ];

    setInventoryAdjustmentItemProcessState(updatedAdjusted);
  };

  const removeBatch = (state: InventoryAdjustmentBatchInfo[]): void => {
    const updatedAdjusted = state.slice(0, -1);
    setInventoryAdjustmentItemProcessState(updatedAdjusted);
  };

  return (
    <Dialog
      open={openModal}
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
        whiteSpace: 'pre-line',
        width: '100%',
      }}
      maxWidth="lg"
      fullWidth
    >
      <div style={{ flexShrink: 0 }}>
        <S.TitleWrapper>
          <S.Title>Inventory Details</S.Title>
        </S.TitleWrapper>
        <Grid container>
          <S.InfoColumnWrapper item container direction="column" gap={1} xs={4}>
            <Grid item>Item</Grid>
            <Grid item>{items.sku_code}</Grid>
          </S.InfoColumnWrapper>
          <S.InfoColumnWrapper item container direction="column" gap={1} xs={4}>
            <Grid item>UOM</Grid>
            <Grid item>{items.product_info.uom}</Grid>
          </S.InfoColumnWrapper>
          <S.InfoColumnWrapper item container direction="column" gap={1} xs={4}>
            <Grid item>QTY</Grid>
            <Grid item>{items.quantity_adjustment}</Grid>
          </S.InfoColumnWrapper>
          <S.FieldRowWrapper item xs={12}>
            <Divider sx={{ borderBottomWidth: 2 }} />
          </S.FieldRowWrapper>
        </Grid>
      </div>
      <div style={{ flexShrink: 1, overflow: 'auto' }}>
        {renderInventoryAdjustmentItemProcessForm(
          inventoryAdjustmentItemProcessState,
        )}
        <Grid container>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.RemoveButton
              onClick={() => removeBatch(inventoryAdjustmentItemProcessState)}
              disabled={inventoryAdjustmentItemProcessState.length === 1}
            >
              REMOVE BATCH
            </S.RemoveButton>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.BlueButtonText
              style={{ fontSize: '12 !important' }}
              onClick={() => addBatch(inventoryAdjustmentItemProcessState)}
            >
              <Add /> ADD BATCH
            </S.BlueButtonText>
          </Grid>
        </Grid>
      </div>
      <S.FooterWrapper style={{ marginTop: 8, flexShrink: 0 }}>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton onClick={onClose}>CANCEL</S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              disabled={isSaveButtonDisabled}
              onClick={handleSave}
            >
              SAVE
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </Dialog>
  );
};
